import React, { useState } from "react";
import {
  Box,
  Typography,
  Link,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Image as ImageIcon,
  LocationOn as LocationIcon,
  Link as LinkIcon,
  AttachFile as AttachFileIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as ExcelIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { api } from "../../../../services/api/api";
import UnifiedFilePreview from "../../../common/UnifiedFilePreview";

/**
 * Componente para renderizar conteúdo rico em comentários
 * - Detecta e renderiza links como clicáveis
 * - Mostra preview de imagens com modal
 * - Detecta links do Google Maps
 * - Preview de PDFs e download de outros arquivos
 */

// Regex patterns
const URL_REGEX = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
const IMAGE_REGEX = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i;
const MAPS_REGEX = /(https?:\/\/(www\.)?(google\.[a-z]+\/maps|maps\.google\.[a-z]+|goo\.gl\/maps)\/[^\s]+)/i;
const FILE_TAG_REGEX = /\[arquivo:([^\]]+)\]/g;
const MENTION_TAG_REGEX = /\[mencao:([^|]+)\|([^\]]+)\]/g;

export default function RichCommentContent({ content, onDownloadFile }) {
  const theme = useTheme();
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState({ name: "", url: "", type: "", driveId: "" });

  // Truncar nome do arquivo (mesmo padrão do DocumentPreview)
  const truncateFileName = (name, maxLength = 40) => {
    if (name.length <= maxLength) return name;
    const ext = name.split('.').pop();
    const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
    const truncated = nameWithoutExt.substring(0, maxLength - ext.length - 4);
    return `${truncated}...${ext}`;
  };

  // Detectar tipo de arquivo pelo nome
  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    
    if (['pdf'].includes(ext)) return <PdfIcon />;
    if (['doc', 'docx'].includes(ext)) return <DocIcon />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <ExcelIcon />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <ImageIcon />;
    return <AttachFileIcon />;
  };

  const getFileColor = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    
    if (['pdf'].includes(ext)) return theme.palette.error.main;
    if (['doc', 'docx'].includes(ext)) return theme.palette.info.main;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return theme.palette.success.main;
    return theme.palette.grey[600];
  };

  // Processa o conteúdo e identifica URLs e tags de arquivo
  const parseContent = (text) => {
    const parts = [];
    let lastIndex = 0;
    
    // Primeiro, processa tags de arquivo [arquivo:id]
    const allMatches = [];
    
    // Encontrar todas as tags de arquivo
    let fileMatch;
    const fileRegex = new RegExp(FILE_TAG_REGEX);
    while ((fileMatch = fileRegex.exec(text)) !== null) {
      allMatches.push({
        index: fileMatch.index,
        length: fileMatch[0].length,
        type: 'file',
        fileId: fileMatch[1],
      });
    }

    // Encontrar todas as tags de menção
    let mentionMatch;
    const mentionRegex = new RegExp(MENTION_TAG_REGEX);
    while ((mentionMatch = mentionRegex.exec(text)) !== null) {
      allMatches.push({
        index: mentionMatch.index,
        length: mentionMatch[0].length,
        type: 'mention',
        userName: mentionMatch[1],
        userId: mentionMatch[2],
      });
    }
    
    // Encontrar todas as URLs
    let urlMatch;
    const urlRegex = new RegExp(URL_REGEX);
    while ((urlMatch = urlRegex.exec(text)) !== null) {
      const url = urlMatch[0];
      let type = 'link';
      
      if (IMAGE_REGEX.test(url)) {
        type = 'image';
      } else if (MAPS_REGEX.test(url)) {
        type = 'maps';
      }
      
      allMatches.push({
        index: urlMatch.index,
        length: url.length,
        type,
        url,
      });
    }
    
    // Ordenar por posição no texto
    allMatches.sort((a, b) => a.index - b.index);
    
    // Construir array de partes
    allMatches.forEach(match => {
      // Adiciona texto antes do match
      if (match.index > lastIndex) {
        parts.push({
          type: "text",
          content: text.substring(lastIndex, match.index),
        });
      }
      
      // Adiciona o match
      if (match.type === 'file') {
        parts.push({ 
          type: 'file', 
          fileId: match.fileId,
        });
      } else if (match.type === 'mention') {
        parts.push({
          type: 'mention',
          userName: match.userName,
          userId: match.userId,
        });
      } else {
        parts.push({ 
          type: match.type, 
          url: match.url,
        });
      }
      
      lastIndex = match.index + match.length;
    });

    // Adiciona texto restante
    if (lastIndex < text.length) {
      parts.push({
        type: "text",
        content: text.substring(lastIndex),
      });
    }

    return parts.length > 0 ? parts : [{ type: "text", content: text }];
  };

  const handleImageClick = (url) => {
    setSelectedImage(url);
    setImageModalOpen(true);
  };

  const handleImageModalClose = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  const getFilePreviewType = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "image";
      case "pdf":
        return "pdf";
      default:
        return "unknown";
    }
  };

  const handleFileClick = async (driveId, fileName) => {
    const fileType = getFilePreviewType(fileName);
    
    if (fileType === "image" || fileType === "pdf") {
      // Buscar downloadUrl do OneDrive (mesmo padrão do FileManager)
      try {
        const response = await api.get(`/files/download/${driveId}`);
        const downloadUrl = response.data.downloadUrl;
        
        if (!downloadUrl) {
          throw new Error('URL de download não encontrada');
        }
        
        setPreviewFile({ name: fileName, url: downloadUrl, type: fileType, driveId });
        setPreviewModalOpen(true);
      } catch (error) {
        console.error("Erro ao buscar preview:", error);
        // Fallback para download
        if (onDownloadFile) {
          onDownloadFile(driveId, fileName);
        }
      }
    } else {
      // Outros tipos: download direto
      if (onDownloadFile) {
        onDownloadFile(driveId, fileName);
      }
    }
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
    setPreviewFile({ name: "", url: "", type: "", driveId: "" });
  };

  const renderPart = (part, index) => {
    switch (part.type) {
      case "text":
        return (
          <span key={index} style={{ whiteSpace: "pre-wrap" }}>
            {part.content}
          </span>
        );

      case "link": {
        // Extrair domínio para exibição limpa
        let displayText;
        try {
          const urlObj = new URL(part.url);
          const domain = urlObj.hostname.replace("www.", "");
          const path = urlObj.pathname !== "/" ? urlObj.pathname : "";
          displayText = domain + (path.length > 20 ? path.substring(0, 17) + "..." : path);
        } catch {
          displayText = part.url.length > 45 ? part.url.substring(0, 42) + "..." : part.url;
        }
        return (
          <Link
            key={index}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <LinkIcon sx={{ fontSize: "0.875rem" }} />
            {displayText}
          </Link>
        );
      }

      case "image":
        return (
          <Box key={index} sx={{ display: "inline-block", my: 1 }}>
            <Box
              component="img"
              src={part.url}
              alt="Imagem anexada"
              onClick={() => handleImageClick(part.url)}
              sx={{
                maxWidth: "200px",
                maxHeight: "200px",
                borderRadius: "8px",
                cursor: "pointer",
                border: `1px solid ${theme.palette.grey[300]}`,
                transition: "all 0.2s",
                display: "block",
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  transform: "scale(1.02)",
                },
              }}
              onError={(e) => {
                // Fallback se imagem não carregar
                e.target.style.display = "none";
                e.target.insertAdjacentHTML(
                  "afterend",
                  `<a href="${part.url}" target="_blank" style="color: ${theme.palette.primary.main}">${part.url}</a>`
                );
              }}
            />
          </Box>
        );

      case "maps":
        return (
          <Link
            key={index}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              textDecoration: "none",
              color: theme.palette.primary.main,
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            <LocationIcon sx={{ fontSize: 16 }} />
            Ver Localização
          </Link>
        );

      case "mention":
        return (
          <Chip
            key={index}
            label={`@${part.userName}`}
            size="small"
            sx={{
              mx: 0.3,
              height: 22,
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "primary.main",
              bgcolor: "rgba(25, 118, 210, 0.08)",
              cursor: "default",
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        );

      case "file":
        // Parser do fileId: formato "nome-do-arquivo.ext|one-drive-id"
        const [fileName, driveId] = part.fileId.split('|');
        const displayName = fileName || 'Arquivo anexado';
        const fileType = getFilePreviewType(displayName);
        const canPreview = fileType === "image" || fileType === "pdf";
        
        return (
          <Chip
            key={index}
            icon={getFileIcon(displayName)}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {displayName.length > 30 ? displayName.substring(0, 27) + '...' : displayName}
                {canPreview && (
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                    • clique para visualizar
                  </Typography>
                )}
              </Box>
            }
            onClick={() => handleFileClick(driveId, displayName)}
            deleteIcon={canPreview ? null : <DownloadIcon />}
            onDelete={canPreview ? undefined : () => onDownloadFile && onDownloadFile(driveId, displayName)}
            clickable
            sx={{
              my: 0.5,
              mx: 0.5,
              backgroundColor: theme.palette.grey[100],
              border: `1px solid ${getFileColor(displayName)}`,
              color: getFileColor(displayName),
              fontWeight: 500,
              maxWidth: '400px',
              "& .MuiChip-icon": {
                color: getFileColor(displayName),
              },
              "&:hover": {
                backgroundColor: theme.palette.grey[200],
                boxShadow: theme.shadows[2],
              },
            }}
          />
        );

      default:
        return null;
    }
  };

  const parts = parseContent(content);

  return (
    <>
      <Typography
        component="div"
        variant="body2"
        sx={{
          lineHeight: 1.6,
          wordBreak: "break-word",
          color: theme.palette.text.primary,
          fontSize: "0.9rem",
        }}
      >
        {parts.map((part, index) => renderPart(part, index))}
      </Typography>

      {/* Modal para visualizar imagem inline (URL no texto) */}
      {selectedImage && (
        <UnifiedFilePreview
          open={imageModalOpen}
          onClose={handleImageModalClose}
          fileName="Imagem"
          fileUrl={selectedImage}
          fileType="image"
        />
      )}

      {/* Modal para preview de arquivos anexados (PDF/imagens) */}
      {previewFile.url && (
        <UnifiedFilePreview
          open={previewModalOpen}
          onClose={handlePreviewClose}
          fileName={previewFile.name}
          fileUrl={previewFile.url}
          fileType={previewFile.type}
        />
      )}
    </>
  );
}
